import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const NON_CANONICAL_BLOG_SLUGS = new Set([
  "standard-telephonique-ia-pour-agence-immobiliere-le-guide-2026",
  "comment-ne-plus-rater-un-seul-appel-acquereur-en-agence-immobiliere",
]);

export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  content: string;
  cover_image_url: string | null;
  status: "draft" | "published";
  published_at: string | null;
  author_id: string | null;
  meta_title: string | null;
  meta_description: string | null;
  created_at: string;
  updated_at: string;
}

export function useBlogPosts() {
  return useQuery({
    queryKey: ["blog_posts", "public"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("status", "published")
        .eq("brand", "immovox")
        .order("published_at", { ascending: false });

      if (error) throw error;
      return (data as BlogPost[]).filter((post) => !NON_CANONICAL_BLOG_SLUGS.has(post.slug));
    },
  });
}

export function useBlogPost(slug: string | undefined) {
  return useQuery({
    queryKey: ["blog_posts", "public", slug],
    queryFn: async () => {
      if (!slug) return null;

      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", slug)
        .eq("status", "published")
        .eq("brand", "immovox")
        .single();

      if (error) {
        if (error.code === "PGRST116") return null;
        throw error;
      }

      return data as BlogPost;
    },
    enabled: !!slug,
  });
}

export interface BlogPostSummary {
  title: string;
  slug: string;
}

export function useBlogPostsSummary() {
  return useQuery({
    queryKey: ["blog_posts", "summary"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("title, slug")
        .eq("status", "published")
        .eq("brand", "immovox")
        .order("published_at", { ascending: false });

      if (error) throw error;
      return (data as BlogPostSummary[]).filter((post) => !NON_CANONICAL_BLOG_SLUGS.has(post.slug));
    },
  });
}
